import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  ViewEncapsulation,
  OnDestroy,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subject, takeUntil } from 'rxjs';

// Services & Interfaces
import { AuthService } from 'src/app/auth/services/auth.service';
import { EntidadLogoService } from '@entidad/services';
import { EntidadService } from '@entidad/services/entidad.service';
import { LayoutService } from '@layout/services/layout.service';
import { CurrentSimpleUser } from '@auth/interfaces/current-simple-user.interface';
import { INavbarData } from './helper';
import { trigger, transition, style, animate } from '@angular/animations';
import { environment } from '@environments/environment';

interface SideNavToggle {
  screenWidth: number;
  collapsed: boolean;
}
export const fadeInOut = trigger('fadeInOut', [
  transition(':enter', [
    style({ opacity: 0 }),
    animate('200ms ease-in-out', style({ opacity: 1 })),
  ]),
  transition(':leave', [style({ opacity: 1 }), animate('0ms', style({ opacity: 0 }))]),
]);
@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [fadeInOut],
})
export class SidebarComponent implements OnInit, OnDestroy {
  @Output() toggleSideNav: EventEmitter<SideNavToggle> = new EventEmitter();

  // State
  navData: INavbarData[] = [];
  collapsed = true;
  isLocked = false;
  screenWidth = 0;
  showDescriptions: boolean = false;
  multiple: boolean = false;

  // Data
  currentUser: CurrentSimpleUser | null = null;
  private destroy$ = new Subject<void>();

  handleClick(item: INavbarData): void {
    if (this.collapsed && item.items && item.items.length > 0) {
      this.layoutService.setSidebarState(true);
      item.expanded = true;
      return;
    }
    this.shrinkItems(item);
    item.expanded = !item.expanded;
  }
  constructor(
    public readonly authService: AuthService,
    private readonly entidadService: EntidadService,
    private readonly entidadLogoService: EntidadLogoService,
    private readonly layoutService: LayoutService,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.screenWidth = window.innerWidth;

    this.layoutService.sidebarOpen$.subscribe((open) => {
      this.isLocked = open;
      this.collapsed = !open;
      this.checkDescriptions();
    });

    this.authService.currentUser$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      this.currentUser = user;
      if (user) {
        this.initMenuItems();
      } else {
        this.navData = [];
        this.entidadService.clearDatosEntidad();
        this.entidadLogoService.clearLogos();
      }
    });

    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.syncExpandedToRoute());
  }
  private checkDescriptions() {
    if (!this.collapsed) {
      const delay = this.isLocked ? 0 : 200;
      setTimeout(() => (this.showDescriptions = true), delay);
    } else {
      this.showDescriptions = false;
    }
  }

  getActiveClass(data: INavbarData): string {
    const route = data.routeLink ?? '';

    if (route === '/' || route === '') {
      return '';
    }

    if (data.items && data.items.length > 0) {
      return data.expanded ? 'active' : '';
    }

    const currentUrl = this.router.url;

    const isExact = currentUrl === `/${route}` || currentUrl === route;
    const isSubRoute = currentUrl.startsWith(`/${route}/`) || currentUrl.startsWith(`${route}/`);

    return isExact || isSubRoute ? 'active' : '';
  }
  isMenuParentActive(data: INavbarData): boolean {
    if (!data.items || data.items.length === 0) return false;
    return data.items.some(
      (child) =>
        child.routeLink &&
        (this.router.url === child.routeLink || this.router.url.startsWith(child.routeLink + '/')),
    );
  }

  private syncExpandedToRoute(): void {
    for (const item of this.navData) {
      if (item.items && item.items.length > 0) {
        const isActive = item.items.some(
          (child) =>
            child.routeLink &&
            (this.router.url === child.routeLink ||
              this.router.url.startsWith(child.routeLink + '/')),
        );
        if (!isActive) {
          item.expanded = false;
        }
      }
    }
  }

  shrinkItems(item: INavbarData): void {
    if (!this.multiple) {
      for (let modelItem of this.navData) {
        if (item !== modelItem && modelItem.expanded) {
          modelItem.expanded = false;
        }
      }
    }
  }
  initMenuItems(): void {
    const can = (p: string) => this.authService.hasPermission(p);
    const isSuperAdmin = this.currentUser?.nombreRol === 'R00T_@DMINISTATOR';
    const rutaMiEntidad = !isSuperAdmin
      ? `/sistema/configuracion/entidades/detalle/mi-entidad/informacion`
      : null;
    this.navData = [
      // 0. DASHBOARD
      {
        routeLink: '/sistema/dashboard',
        icon: 'BarChart3',
        label: 'Dashboard',
        visible: can('dashboard:ver'),
      },

      // 1. ACTAS
      {
        routeLink: 'actas',
        icon: 'file-search',
        label: 'Actas',
        visible: can('actas:ver') || can('actas_eliminadas:consultar'),
        items: [
          {
            routeLink: '/sistema/actas/consultasAdmin-v2',
            label: 'Consulta de Actas',
            visible: can('actas:ver'),
          },
          {
            routeLink: '/sistema/actas/consultasAdmin-v2/eliminadas',
            label: 'Actas eliminadas',
            visible: can('actas_eliminadas:consultar'),
          },
        ],
      },

      // 2. REPORTES
      {
        routeLink: '/sistema/reportes',
        icon: 'clipboard-plus',
        label: 'Reportes',
        visible: can('reportes:ver'),
      },
      {
        routeLink: 'notificaciones',
        icon: 'bell',
        label: 'Notificaciones',
        // 'notificaciones:ver' es el buzon interno (la campana), no el modulo de
        // gestion: el menu se muestra si tiene acceso a alguna de sus pantallas.
        visible: true,
        items: [
          {
            routeLink: '/sistema/notificaciones/historial',
            label: 'Mis Notificaciones',
            visible: true,
          },
          {
            routeLink: '/sistema/notificaciones/dashboard',
            label: 'Dashboard',
            visible: can('notificaciones:trazabilidad'),
          },
          {
            routeLink: '/sistema/notificaciones/tipos',
            label: 'Tipos',
            visible: can('notificaciones:tipos'),
          },
          {
            routeLink: '/sistema/notificaciones/variables',
            label: 'Variables',
            visible: can('notificaciones:variables'),
          },
          {
            routeLink: '/sistema/notificaciones/medios',
            label: 'Medios',
            visible: can('notificaciones:medios'),
          },
          {
            routeLink: '/sistema/notificaciones/plantillas',
            label: 'Plantillas',
            visible: can('notificaciones:plantillas'),
          },
          {
            routeLink: '/sistema/notificaciones/trazabilidad',
            label: 'Trazabilidad',
            visible: can('notificaciones:trazabilidad'),
          },
          {
            routeLink: '/sistema/notificaciones/diagnostico',
            label: 'Diagnóstico',
            visible: can('notificaciones:diagnostico') || can('entidades:global'),
          },
        ],
      },

      // 3. JUZGAMIENTO
      {
        routeLink: 'juzgamiento',
        icon: 'scale',
        label: 'Juzgamientos',
        visible: can('juzgamiento:ver'),
        items: [
          {
            routeLink: '/sistema/juzgamiento/pases',
            label: 'Procesos',
            visible: can('juzgamiento:ver'),
          },
          {
            routeLink: '/sistema/juzgamiento/mis-instrucciones',
            label: 'Mis Instrucciones',
            visible: can('juzgamiento:ver'),
          },
          {
            routeLink: '/sistema/juzgamiento/plantillas-doc',
            label: 'Plantillas Documentales',
            visible: can('juzgamiento:plantillas-doc'),
          },
        ],
      },

      {
        routeLink: 'retenciones',
        icon: 'retenciones-icon',
        label: 'Retenciones',
        visible: can('licencias_retenciones:consultar') || can('licencias:consultar'),
        items: [
          {
            routeLink: '/sistema/retenciones/',
            label: 'Licencias Retenidas',
            visible: can('licencias_retenciones:consultar'),
          },
          {
            routeLink: '/sistema/licencias',
            label: 'Licencias',
            visible: can('licencias:consultar'),
          },
          {
            routeLink: '/sistema/licencias/revision',
            label: 'Bandeja de revisión',
            visible: can('licencias:consultar'),
          },
        ],
      },

      // 4. CANCHONES
      {
        routeLink: 'canchones',
        icon: 'warehouse',
        label: 'Canchones y Grúas',
        visible: can('canchones:ver') || can('gruas:consultar'),
        items: [
          {
            routeLink: '/sistema/canchones',
            label: 'Administración de Canchones',
            visible: can('canchones:ver'),
          },
          {
            routeLink: '/sistema/gruas',
            label: 'Administración de Grúas',
            visible: can('gruas:consultar'),
          },
        ],
      },

      // 5. EQUIPOS
      {
        routeLink: 'equipos',
        icon: 'monitor-smartphone',
        label: 'Equipos',
        visible: can('equipos:ver'),
        items: [
          {
            routeLink: '/sistema/tipos-equipos',
            label: 'Tipos',
            visible: can('tipos_equipos:ver'),
          },
          {
            routeLink: '/sistema/equipos-medicion',
            label: 'Mantenimiento',
            visible: can('mantenimiento:ver'),
          },
          {
            routeLink: '/sistema/dispositivos',
            label: 'Dispositivos',
            visible: can('dispositivos:ver'),
          },
        ],
      },

      // 5. USUARIOS
      {
        routeLink: 'usuarios',
        icon: 'user-cog',
        label: 'Usuarios',
        visible: can('usuarios:ver') || can('roles:ver'),
        items: [
          {
            routeLink: '/sistema/usuarios/roles',
            label: 'Gestión de Roles',
            visible: can('roles:ver'),
          },
          {
            routeLink: '/sistema/usuarios',
            label: 'Gestión de Usuarios',
            visible: can('usuarios:ver'),
          },
          {
            routeLink: '/sistema/relacion-gestor-juez',
            label: 'Relación Gestores - Jueces',
            visible: true,
          },
        ],
      },

      // 6. PARÁMETROS
      {
        routeLink: 'parametros',
        icon: 'ruler',
        label: 'Parámetros',
        visible: can('unidades_fijas:ver') || can('nomencladores:ver') || can('recursos:gestionar'),
        items: [
          {
            routeLink: '/sistema/actas/administracion/unidades-tributarias', // Ajusta si moviste esto
            label: 'Unidades Fijas',
            visible: can('unidades_fijas:ver'),
          },
          {
            routeLink: '/sistema/nomencladores',
            label: 'Nomencladores',
            visible: can('nomencladores:ver'),
          },
          // {
          //   routeLink: '/sistema/recursos',
          //   label: 'Recursos',
          //   visible: can('recursos:gestionar'),
          // },
        ],
      },

      // 7. PAGOS
      {
        routeLink: 'pagos-recaudacion',
        icon: 'landmark',
        label: 'Pagos y Recaudación',
        visible: can('lotes:ver'),
        items: [
          {
            routeLink: '/sistema/pagos-recaudacion/generacion',
            label: 'Generación de Archivos',
            visible: can('lotes:generar'),
          },
          {
            routeLink: '/sistema/import-pagos-recaudacion/importacion',
            label: 'Importación de Pagos',
            visible: can('lotes:procesar'),
          },
        ],
      },

      // 8. ENTIDAD
      {
        routeLink: 'configuracion',
        icon: 'settings',
        label: 'Configuración',
        // Visible si tiene permiso de listar, o ver su propia entidad, o geo, o tipos
        visible: true /*
          can('entidades:listar') ||
          can('entidad:ver') ||
          can('geografia:ver') ||
          can('tipos_entidad:ver')*/,
        items: [
          // A. PARA EL SUPER ADMIN
          {
            routeLink: '/sistema/configuracion/entidades/listado',
            label: 'Municipios y Juzgados',
            visible: can('config:global') || isSuperAdmin,
          },
          {
            routeLink: '/sistema/configuracion/tipos',
            label: 'Tipos de Entidad',
            visible: can('config:global') || isSuperAdmin,
          },
          {
            routeLink: rutaMiEntidad || '',
            label: 'Mi Institución',
            visible: !isSuperAdmin && !!rutaMiEntidad,
          },
          {
            routeLink: '/sistema/configuracion/divisiones-geograficas',
            label: 'Geografía',
            visible: can('geografia:ver'),
          },
          {
            routeLink: '/sistema/configuracion/catalogo-permisos',
            label: 'Catálogo de Permisos',
            visible: can('config:global'),
          },
          // {
          //   routeLink: '/sistema/configuracion/admin/test-imagenes',
          //   label: 'Pruebas de Imágenes (DEV)',
          //   visible: !environment.production,
          // },
        ],
      },
    ];
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
